import React, { useState } from 'react';
import useAppSelector from '../../app/redux/useAppSelector';
import { MenuItem } from '../../components/Menu';
import OverflowMenu from '../../components/OverflowMenu';
import { gql } from '../../graphql/generated/gql';
import trashIcon from '../../icons/non-prefilled/16x16/trash.svg';
import type { ExtractReactionThreadTypes } from '../../utils/post/threadUtils';
import MagneticWallSheet from '../MagneticWallSheet';
import { CommentThreadSheetContext } from './CommentThreadSheet';
import * as styles from './ReactionThreadSheet.module.scss';
import ThreadReactionList from './ThreadReactionList';

const THREAD_FRAGMENT = gql(`
  fragment ReactionThreadSheet_thread on RootThreadFields {
    id
    author {
      id
    }
    ... on ReactionThread {
      reactions {
        id
        ...ThreadReactionList_reactions
      }
    }
    ... on PublicReactionThread {
      reactions {
        id
        ...ThreadReactionList_reactions
      }
    }
  }
`);

type ReactionThreadSheetProps = {
  thread: ExtractReactionThreadTypes<typeof THREAD_FRAGMENT>;
  onClose: () => void;
  deleteReactionThread: () => void;
} & Pick<
  React.ComponentProps<typeof ThreadReactionList>,
  'addReaction' | 'deleteReaction'
>;

const ReactionThreadSheet = ({
  onClose,
  thread,
  deleteReactionThread,
  addReaction,
  deleteReaction,
}: ReactionThreadSheetProps) => {
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const currentUser = useAppSelector((state) => state.session.user);
  const isAuthor = thread.author.id === currentUser.id;

  return (
    <MagneticWallSheet
      onClose={onClose}
      title="Reaction"
      headerContent={
        <CommentThreadSheetContext id={thread.id} type="comment" highlight />
      }
      overflowMenu={
        isAuthor ? (
          <OverflowMenu>
            <MenuItem danger icon={trashIcon} onClick={deleteReactionThread}>
              Delete all
            </MenuItem>
          </OverflowMenu>
        ) : null
      }
    >
      {thread.reactions.length > 0 && (
        <div className={styles.reactions}>
          <ThreadReactionList
            thread={thread}
            expanded
            showAdditionalNode
            onEmojiPickerOpenChange={setIsEmojiPickerOpen}
            isEmojiPickerOpen={isEmojiPickerOpen}
            onAdditionalNodeClick={() => setIsEmojiPickerOpen((open) => !open)}
            hideOverflowCount={false}
            addReaction={addReaction}
            deleteReaction={deleteReaction}
          />
        </div>
      )}
    </MagneticWallSheet>
  );
};

export default ReactionThreadSheet;
